'use client';

import { Button } from '@ui/components/button';
import { Card } from '@ui/components/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ui/components/dialog';
import { Input } from '@ui/components/input';
import { Label } from '@ui/components/label';
import { AppCard, PageLayout } from '@ui/components/layouts';
import { text as sharedText } from '@shared/text';
import { text } from '@modules/my-gardens/text';

import { FaIcon } from '@micks-yard/icons/FaIcon';
import { faCopy } from '@micks-yard/icons/solid/faCopy';
import { faEye } from '@micks-yard/icons/solid/faEye';
import { faPenToSquare } from '@micks-yard/icons/solid/faPenToSquare';
import { faTrash } from '@micks-yard/icons/solid/faTrash';
import type { MyGardensProps } from '@modules/my-gardens/containers/my_gardens_container';
import { PageContentLoading } from '@/src/components/PageContentLoading';

const rowActionClassName = 'text-muted-foreground hover:bg-green-100 hover:text-green-800';

export function MyGardens({
  isLoading,
  isSaving,
  gardens,
  onStartCreating,
  onStartEditing,
  onDuplicateGarden,
  onConfirmDelete,
  onViewGarden,
  canDeleteGarden,
  createDialogOpen,
  newGardenName,
  onNewGardenNameChange,
  onCreateSubmit,
  onCreateCancel,
  editDialogOpen,
  editName,
  onEditNameChange,
  onEditSubmit,
  onEditCancel,
  deleteDialogOpen,
  gardenToDeleteName,
  onDeleteConfirm,
  onDeleteCancel,
}: MyGardensProps) {
  if (isLoading) {
    return <PageContentLoading />;
  }

  return (
    <>
      <PageLayout title={text.title} maxWidth={896}>
        <AppCard className="p-4">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold">{text.sectionTitle}</h3>
            <Button size="sm" onClick={onStartCreating} disabled={isSaving}>
              {text.newGarden}
            </Button>
          </div>

          {gardens.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8">
              <p className="text-muted-foreground">{text.emptyTitle}</p>
              <p className="text-muted-foreground text-sm">{text.emptyHint}</p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {gardens.map((garden) => (
                <Card key={garden._id} className="border-border bg-background p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="font-semibold">{garden.name}</span>
                      <p className="text-muted-foreground text-sm">
                        {text.gardenSummary(garden.rows, garden.columns, garden.plants.length)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        className={rowActionClassName}
                        onClick={() => onStartEditing(garden._id, garden.name)}
                        disabled={isSaving}
                        aria-label={text.renameGarden}>
                        <FaIcon icon={faPenToSquare} className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        className={rowActionClassName}
                        onClick={() => onDuplicateGarden(garden._id)}
                        disabled={isSaving}
                        aria-label={text.duplicateGarden}>
                        <FaIcon icon={faCopy} className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        className={rowActionClassName}
                        onClick={() => onViewGarden(garden._id)}
                        aria-label={text.viewGarden}>
                        <FaIcon icon={faEye} className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        className={rowActionClassName}
                        onClick={() => onConfirmDelete(garden._id)}
                        disabled={isSaving || !canDeleteGarden}
                        aria-label={
                          canDeleteGarden ? text.deleteGarden : text.cannotDeleteLastGarden
                        }>
                        <FaIcon icon={faTrash} className="size-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </AppCard>
      </PageLayout>

      <Dialog open={createDialogOpen} onOpenChange={(open) => !open && onCreateCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{text.createDialogTitle}</DialogTitle>
            <DialogDescription>{text.createDialogDescription}</DialogDescription>
          </DialogHeader>
          <form onSubmit={onCreateSubmit}>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="garden-name">{text.gardenNameLabel}</Label>
                <Input
                  id="garden-name"
                  value={newGardenName}
                  onChange={(event) => onNewGardenNameChange(event.target.value)}
                  placeholder={text.gardenNamePlaceholder}
                  autoFocus
                  disabled={isSaving}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onCreateCancel} disabled={isSaving}>
                {sharedText.cancel}
              </Button>
              <Button type="submit" disabled={!newGardenName.trim() || isSaving}>
                {text.createGarden}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialogOpen} onOpenChange={(open) => !open && onEditCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{text.editDialogTitle}</DialogTitle>
            <DialogDescription>{text.editDialogDescription}</DialogDescription>
          </DialogHeader>
          <form onSubmit={onEditSubmit}>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="edit-garden-name">{text.gardenNameLabel}</Label>
                <Input
                  id="edit-garden-name"
                  value={editName}
                  onChange={(event) => onEditNameChange(event.target.value)}
                  placeholder={text.gardenNamePlaceholder}
                  autoFocus
                  disabled={isSaving}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onEditCancel} disabled={isSaving}>
                {sharedText.cancel}
              </Button>
              <Button type="submit" disabled={!editName.trim() || isSaving}>
                {text.saveChanges}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={(open) => !open && onDeleteCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{text.deleteDialogTitle}</DialogTitle>
            <DialogDescription>
              {gardenToDeleteName ? text.deleteDialogDescription(gardenToDeleteName) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onDeleteCancel} disabled={isSaving}>
              {sharedText.cancel}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={onDeleteConfirm}
              disabled={isSaving}>
              {text.deleteGarden}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
